import React, { useState, useEffect, useMemo } from "react";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	Button,
	Alert,
	Grid,
	Chip,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Checkbox,
	ListItemText,
	OutlinedInput,
	FormHelperText,
} from "@mui/material";
import {
	Edit as EditIcon,
	Check as CheckIcon,
	X as XIcon,
	Search as SearchIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { getIndianStates, getDistrictsForStateName, normalizeSchoolLocation } from "../../data/indiaStateDistrictOptions";

const INDIAN_STATES = getIndianStates();

const SchoolApprovals = () => {
	const { supabase } = useAuth();
	const [pendingSchools, setPendingSchools] = useState([]);
	const [filteredSchools, setFilteredSchools] = useState([]);
	const [selectedSchool, setSelectedSchool] = useState(null);
	const [openEditDialog, setOpenEditDialog] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [schoolTypes, setSchoolTypes] = useState([]);
	const [grades, setGrades] = useState([]);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [searchTerm, setSearchTerm] = useState("");

	const [editForm, setEditForm] = useState({
		name: "",
		address: "",
		school_type_id: "",
		state: "",
		district: "",
		website_link: "",
		landline: "",
		principal_name: "",
		principal_contact: "",
		finance_incharge: "",
		finance_incharge_contact: "",
		store_incharge: "",
		store_incharge_contact: "",
		primary_email: "",
		map_location: "",
		selected_grades: [],
		school_unique_id: "",
	});

	const [formErrors, setFormErrors] = useState({
		name: false,
		address: false,
		school_type_id: false,
		state: false,
		district: false,
		selected_grades: false,
	});

	const [formErrorMessage, setFormErrorMessage] = useState("");

	const districtOptions = useMemo(() => {
		const list = getDistrictsForStateName(editForm.state);
		const d = editForm.district;
		if (d && !list.includes(d)) return [d, ...list];
		return list;
	}, [editForm.state, editForm.district]);

	useEffect(() => {
		fetchPendingSchools();
		fetchSchoolTypes();
		fetchGrades();
	}, []);

	useEffect(() => {
		applyFilters();
	}, [searchTerm, pendingSchools]);

	const fetchPendingSchools = async () => {
		try {
			const { data, error } = await supabase
				.from("schools")
				.select(
					`
					*,
					school_types (
						id,
						name
					),
					school_grades (
						id,
						grade_id,
						grades:grade_id (
							id,
							name
						)
					)
				`
				)
				.eq("status", "Pending")
				.order("created_at", { ascending: false });

			if (error) throw error;
			setPendingSchools(data || []);
		} catch (err) {
			console.error("Error fetching pending schools:", err);
			setErrorMessage("Error loading pending schools");
		}
	};

	const fetchSchoolTypes = async () => {
		try {
			const { data, error } = await supabase
				.from("school_types")
				.select("*")
				.order("name");

			if (error) throw error;
			setSchoolTypes(data || []);
		} catch (err) {
			console.error("Error fetching school types:", err);
		}
	};

	const fetchGrades = async () => {
		try {
			const { data, error } = await supabase
				.from("grades")
				.select("*")
				.order("name");

			if (error) throw error;
			setGrades(data || []);
		} catch (err) {
			console.error("Error fetching grades:", err);
		}
	};

	const fetchSchoolGrades = async (schoolId) => {
		try {
			const { data, error } = await supabase
				.from("school_grades")
				.select("*")
				.eq("school_id", schoolId);

			if (error) throw error;

			// Extract grade IDs for the form
			const gradeIds = data.map((sg) => sg.grade_id);
			setEditForm((prev) => ({
				...prev,
				selected_grades: gradeIds,
			}));
		} catch (err) {
			console.error("Error fetching school grades:", err);
		}
	};

	const applyFilters = () => {
		let filtered = [...pendingSchools];

		if (searchTerm) {
			const searchLower = searchTerm.toLowerCase();
			filtered = filtered.filter(
				(school) =>
					school.name?.toLowerCase().includes(searchLower) ||
					school.district?.toLowerCase().includes(searchLower) ||
					school.school_types?.name?.toLowerCase().includes(searchLower)
			);
		}

		setFilteredSchools(filtered);
	};

	const handleApprove = async (schoolId) => {
		try {
			const { error } = await supabase
				.from("schools")
				.update({
					status: "Active",
					activated_at: new Date().toISOString(),
				})
				.eq("id", schoolId);

			if (error) throw error;

			setSuccessMessage("School approved and activated successfully");
			await fetchPendingSchools();

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error approving school:", err);
			setErrorMessage("Error approving school");
		}
	};

	const handleReject = async (schoolId) => {
		try {
			const { error } = await supabase
				.from("schools")
				.update({
					status: "Rejected",
					deactivated_at: new Date().toISOString(),
				})
				.eq("id", schoolId);

			if (error) throw error;

			setSuccessMessage("School rejected successfully");
			await fetchPendingSchools();

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error rejecting school:", err);
			setErrorMessage("Error rejecting school");
		}
	};

	const handleEditClick = (school) => {
		setSelectedSchool(school);
		const { state, district } = normalizeSchoolLocation(
			school.state,
			school.district
		);
		setEditForm({
			name: school.name || "",
			address: school.address || "",
			school_type_id: school.school_type_id || "",
			state,
			district,
			website_link: school.website_link || "",
			landline: school.landline || "",
			principal_name: school.principal_name || "",
			principal_contact: school.principal_contact || "",
			finance_incharge: school.finance_incharge || "",
			finance_incharge_contact: school.finance_incharge_contact || "",
			store_incharge: school.store_incharge || "",
			store_incharge_contact: school.store_incharge_contact || "",
			primary_email: school.primary_email || "",
			map_location: school.map_location || "",
			selected_grades: [],
			school_unique_id: school.school_unique_id || "",
		});
		fetchSchoolGrades(school.id);
		setOpenEditDialog(true);
	};

	const handleSave = async () => {
		try {
			// Validate mandatory fields
			const errors = {
				name: !editForm.name.trim(),
				school_type_id: !editForm.school_type_id,
				state: !editForm.state,
				district: !editForm.district,
				address: !editForm.address.trim(),
				selected_grades: editForm.selected_grades.length === 0,
			};

			setFormErrors(errors);

			if (
				errors.name ||
				errors.school_type_id ||
				errors.state ||
				errors.district ||
				errors.address ||
				errors.selected_grades
			) {
				setFormErrorMessage("Please fill in all required fields");
				return;
			}

			setFormErrorMessage("");

			const formData = {
				name: editForm.name,
				address: editForm.address,
				school_type_id: editForm.school_type_id,
				state: editForm.state,
				district: editForm.district,
				website_link: editForm.website_link,
				landline: editForm.landline,
				principal_name: editForm.principal_name,
				principal_contact: editForm.principal_contact,
				finance_incharge: editForm.finance_incharge,
				finance_incharge_contact: editForm.finance_incharge_contact,
				store_incharge: editForm.store_incharge,
				store_incharge_contact: editForm.store_incharge_contact,
				primary_email: editForm.primary_email,
				map_location: editForm.map_location,
				school_unique_id: editForm.school_unique_id?.trim()
					? editForm.school_unique_id.trim()
					: null,
				updated_at: new Date().toISOString(),
			};

			// Update school
			const { error } = await supabase
				.from("schools")
				.update(formData)
				.eq("id", selectedSchool.id);

			if (error) throw error;

			// Handle school grades association
			// First delete all existing grades for this school
			const { error: deleteError } = await supabase
				.from("school_grades")
				.delete()
				.eq("school_id", selectedSchool.id);

			if (deleteError) {
				console.error("Error deleting old school grades:", deleteError);
			}

			// Now insert the new grade associations
			const gradeAssociations = editForm.selected_grades.map((gradeId) => ({
				school_id: selectedSchool.id,
				grade_id: gradeId,
			}));

			if (gradeAssociations.length > 0) {
				const { error: insertError } = await supabase
					.from("school_grades")
					.insert(gradeAssociations);

				if (insertError) {
					console.error("Error inserting school grades:", insertError);
				}
			}

			setSuccessMessage("School details updated successfully");
			await fetchPendingSchools();
			setOpenEditDialog(false);

			// Reset form errors
			setFormErrors({
				name: false,
				address: false,
				school_type_id: false,
				district: false,
				selected_grades: false,
			});
			setFormErrorMessage("");

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error updating school:", err);
			setErrorMessage("Error updating school details");
		}
	};

	const getGradeNames = (school) => {
		if (!school.school_grades || school.school_grades.length === 0) {
			return "None";
		}

		const gradeNames = school.school_grades
			.filter((sg) => sg.grades)
			.map((sg) => sg.grades.name)
			.sort();

		if (gradeNames.length === 0) return "None";

		return gradeNames.length <= 3
			? gradeNames.join(", ")
			: `${gradeNames.slice(0, 3).join(", ")}... +${
					gradeNames.length - 3
			  } more`;
	};

	const formatDate = (dateString) => {
		return dateString
			? new Date(dateString).toLocaleDateString("en-GB")
			: "N/A";
	};

	return (
		<Box>
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}>
				<Typography variant="h6">Pending School Approvals</Typography>
				<TextField
					variant="outlined"
					size="small"
					placeholder="Search schools..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					sx={{ width: 300 }}
					InputProps={{
						startAdornment: <SearchIcon size={20} style={{ marginRight: 8 }} />,
					}}
				/>
			</Box>

			<Paper
				elevation={0}
				sx={{
					p: 2,
					border: "1px solid #e5e7eb",
					borderRadius: "8px",
				}}>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>School Name</TableCell>
								<TableCell>School ID</TableCell>
								<TableCell>Type</TableCell>
								<TableCell>District</TableCell>
								<TableCell>Grades</TableCell>
								<TableCell>Created Date</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredSchools
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((school) => (
									<TableRow key={school.id}>
										<TableCell>
											<Box>
												<Typography variant="body2" fontWeight="medium">
													{school.name}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{school.address}
												</Typography>
											</Box>
										</TableCell>
										<TableCell>{school.school_unique_id || "N/A"}</TableCell>
										<TableCell>
											<Chip
												label={school.school_types?.name || "N/A"}
												size="small"
												variant="outlined"
											/>
										</TableCell>
										<TableCell>{school.district || "N/A"}</TableCell>
										<TableCell>{getGradeNames(school)}</TableCell>
										<TableCell>{formatDate(school.created_at)}</TableCell>
										<TableCell>
											<IconButton
												size="small"
												onClick={() => handleEditClick(school)}
												sx={{ mr: 1 }}
												title="Edit Details">
												<EditIcon size={16} />
											</IconButton>
											<IconButton
												size="small"
												color="success"
												onClick={() => handleApprove(school.id)}
												sx={{ mr: 1 }}
												title="Approve School">
												<CheckIcon size={16} />
											</IconButton>
											<IconButton
												size="small"
												color="error"
												onClick={() => handleReject(school.id)}
												title="Reject School">
												<XIcon size={16} />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
						</TableBody>
					</Table>
				</TableContainer>

				{filteredSchools.length === 0 && (
					<Box sx={{ py: 4, textAlign: "center" }}>
						<Typography color="text.secondary">
							{pendingSchools.length === 0
								? "No pending schools for approval"
								: "No schools found matching the search criteria"}
						</Typography>
					</Box>
				)}

				<Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
					<TablePagination
						component="div"
						count={filteredSchools.length}
						page={page}
						onPageChange={(event, newPage) => setPage(newPage)}
						rowsPerPage={rowsPerPage}
						onRowsPerPageChange={(event) => {
							setRowsPerPage(parseInt(event.target.value, 10));
							setPage(0);
						}}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Box>
			</Paper>

			{/* Edit School Dialog */}
			<Dialog
				open={openEditDialog}
				onClose={() => setOpenEditDialog(false)}
				maxWidth="md"
				fullWidth>
				<DialogTitle>Edit School Details</DialogTitle>
				<DialogContent>
					{formErrorMessage && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{formErrorMessage}
						</Alert>
					)}
					<Grid container spacing={2} sx={{ mt: 1 }}>
						<Grid item xs={12} sm={6}>
							<TextField
								label="School Name"
								fullWidth
								required
								value={editForm.name}
								onChange={(e) => {
									setEditForm({ ...editForm, name: e.target.value });
									if (e.target.value.trim()) {
										setFormErrors({ ...formErrors, name: false });
									}
								}}
								error={formErrors.name}
								helperText={formErrors.name ? "School name is required" : ""}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControl fullWidth required error={formErrors.school_type_id}>
								<InputLabel>School Type</InputLabel>
								<Select
									value={editForm.school_type_id}
									label="School Type"
									onChange={(e) => {
										setEditForm({
											...editForm,
											school_type_id: e.target.value,
										});
										if (e.target.value) {
											setFormErrors({ ...formErrors, school_type_id: false });
										}
									}}>
									{schoolTypes.map((type) => (
										<MenuItem key={type.id} value={type.id}>
											{type.name}
										</MenuItem>
									))}
								</Select>
								{formErrors.school_type_id && (
									<Typography
										variant="caption"
										color="error"
										sx={{ ml: 2, mt: 0.5 }}>
										School type is required
									</Typography>
								)}
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControl fullWidth required error={formErrors.state}>
								<InputLabel>State / UT</InputLabel>
								<Select
									value={editForm.state}
									label="State / UT"
									onChange={(e) => {
										const v = e.target.value;
										setEditForm({ ...editForm, state: v, district: "" });
										if (v) {
											setFormErrors({ ...formErrors, state: false, district: false });
										}
									}}>
									<MenuItem value="">
										<em>Select state</em>
									</MenuItem>
									{INDIAN_STATES.map((s) => (
										<MenuItem key={s.stateCode} value={s.stateName}>
											{s.stateName}
										</MenuItem>
									))}
								</Select>
								{formErrors.state && (
									<Typography
										variant="caption"
										color="error"
										sx={{ ml: 2, mt: 0.5 }}>
										State is required
									</Typography>
								)}
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6}>
							<FormControl
								fullWidth
								required
								error={formErrors.district}
								disabled={!editForm.state}>
								<InputLabel>District</InputLabel>
								<Select
									value={editForm.district}
									label="District"
									onChange={(e) => {
										setEditForm({ ...editForm, district: e.target.value });
										if (e.target.value) {
											setFormErrors({ ...formErrors, district: false });
										}
									}}>
									<MenuItem value="">
										<em>
											{editForm.state ? "Select district" : "Select state first"}
										</em>
									</MenuItem>
									{districtOptions.map((district) => (
										<MenuItem key={district} value={district}>
											{district}
										</MenuItem>
									))}
								</Select>
								{formErrors.district && (
									<Typography
										variant="caption"
										color="error"
										sx={{ ml: 2, mt: 0.5 }}>
										District is required
									</Typography>
								)}
							</FormControl>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="School Unique ID"
								fullWidth
								value={editForm.school_unique_id}
								onChange={(e) =>
									setEditForm({ ...editForm, school_unique_id: e.target.value })
								}
								helperText="Enter a unique identifier for the school"
							/>
						</Grid>

						{/* Grades Multi-select */}
						<Grid item xs={12}>
							<FormControl
								fullWidth
								required
								error={formErrors.selected_grades}>
								<InputLabel>Grades</InputLabel>
								<Select
									multiple
									value={editForm.selected_grades}
									onChange={(e) => {
										setEditForm({
											...editForm,
											selected_grades: e.target.value,
										});
										if (e.target.value.length > 0) {
											setFormErrors({ ...formErrors, selected_grades: false });
										}
									}}
									input={<OutlinedInput label="Grades" />}
									renderValue={(selected) => {
										const selectedGradeNames = selected
											.map((id) => {
												const grade = grades.find((g) => g.id === id);
												return grade ? grade.name : "";
											})
											.filter(Boolean);

										return selectedGradeNames.join(", ");
									}}>
									{grades.map((grade) => (
										<MenuItem key={grade.id} value={grade.id}>
											<Checkbox
												checked={
													editForm.selected_grades.indexOf(grade.id) > -1
												}
											/>
											<ListItemText primary={grade.name} />
										</MenuItem>
									))}
								</Select>
								{formErrors.selected_grades && (
									<FormHelperText error>
										At least one grade is required
									</FormHelperText>
								)}
							</FormControl>
						</Grid>

						<Grid item xs={12}>
							<TextField
								label="Address"
								fullWidth
								required
								multiline
								rows={2}
								value={editForm.address}
								onChange={(e) => {
									setEditForm({ ...editForm, address: e.target.value });
									if (e.target.value.trim()) {
										setFormErrors({ ...formErrors, address: false });
									}
								}}
								error={formErrors.address}
								helperText={formErrors.address ? "Address is required" : ""}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Principal Name"
								fullWidth
								value={editForm.principal_name}
								onChange={(e) =>
									setEditForm({ ...editForm, principal_name: e.target.value })
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Principal Contact"
								fullWidth
								value={editForm.principal_contact}
								onChange={(e) =>
									setEditForm({
										...editForm,
										principal_contact: e.target.value,
									})
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Landline"
								fullWidth
								value={editForm.landline}
								onChange={(e) =>
									setEditForm({ ...editForm, landline: e.target.value })
								}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="Primary Email"
								fullWidth
								value={editForm.primary_email}
								onChange={(e) =>
									setEditForm({ ...editForm, primary_email: e.target.value })
								}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Website Link"
								fullWidth
								value={editForm.website_link}
								onChange={(e) =>
									setEditForm({ ...editForm, website_link: e.target.value })
								}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								label="Google Maps Location"
								fullWidth
								placeholder="Enter location coordinates or address"
								value={editForm.map_location || ""}
								onChange={(e) =>
									setEditForm({ ...editForm, map_location: e.target.value })
								}
								helperText="Enter coordinates (e.g., 12.9716,77.5946) or full address"
							/>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
					<Button onClick={handleSave} variant="contained">
						Save Changes
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default SchoolApprovals;
